'use client';

import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "size", "variant", "loading", "loadingText", "inverse", "asChild", "children"];
import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import Loader from "../../Loader";
import { cn } from "../utils";

// ?: Revisar el Figma para ver el dark/light mode del botón "White"
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var buttonVariants = /*#__PURE__*/cva('', {
  variants: {
    variant: {
      primary: 'tw-btn-primary disabled:tw-btn-disabled',
      'primary-inverted': 'tw-btn-inverse disabled:tw-btn-disabled',
      payment: 'tw-btn-payment disabled:tw-btn-disabled',
      'payment-inverted': 'tw-btn-payment-inverse disabled:tw-btn-disabled',
      deactivate: 'tw-btn-deactivate disabled:tw-btn-disabled',
      'deactivate-inverted': 'tw-btn-deactivate-inverse disabled:tw-btn-disabled',
      "delete": 'tw-btn-delete disabled:tw-btn-disabled',
      'delete-inverted': 'tw-btn-delete-inverse disabled:tw-btn-disabled',
      white: 'tw-btn-primary tw-btn-white disabled:tw-btn-disabled',
      cancel: 'tw-font-link3 tw-py-1.2 tw-px-2 tw-text-neutral-light-700 dark:tw-text-neutral-dark-500 disabled:tw-pointer-events-none disabled:tw-opacity-60',
      icon: /*#__PURE__*/cn('tw-px-1.2 tw-py-0.8', 'tw-bg-neutral-light-200 tw-text-neutral-light-800 dark:tw-bg-neutral-dark-700 dark:tw-text-neutral-light-100', 'hover:tw-bg-primary-light-100 hover:tw-text-primary-light-500 dark:hover:tw-bg-primary-dark-100 dark:hover:tw-text-primary-light-500', 'disabled:tw-bg-neutral-light-400 disabled:tw-text-neutral-light-500 disabled:hover:tw-bg-neutral-light-400 disabled:hover:tw-text-neutral-light-500', 'dark:disabled:tw-bg-neutral-dark-400 dark:disabled:tw-text-neutral-dark-300 dark:disabled:hover:tw-bg-neutral-dark-400 dark:disabled:hover:tw-text-neutral-dark-300'),
      neutral: /*#__PURE__*/cn('tw-rounded-1 tw-font-link1 tw-flex tw-items-center', 'tw-bg-neutral-light-100 tw-justify-center tw-py-0.8 tw-px-1.2', 'tw-border-solid tw-border-0.1 tw-border-neutral-light-500 tw-appearance-none', 'hover:tw-text-primary-light-500 hover:tw-border-primary-light-500 hover:tw-border-primary-light-500',
      // programmatically set the active state using the aria-pressed attribute
      'aria-[pressed=true]:tw-text-primary-light-500 aria-[pressed=true]:tw-border-primary-light-500 aria-[pressed=true]:tw-border-primary-light-500', 'aria-[pressed=true]:active:tw-text-neutral-light-100', 'active:tw-text-neutral-light-100 active:tw-bg-primary-light-500', 'disabled:tw-btn-disabled disabled:tw-bg-neutral-light-400 disabled:tw-text-neutral-light-600 disabled:tw-border-neutral-light-400')
    },
    loading: {
      "true": 'tw-pointer-events-none tw-cursor-default'
    },
    size: {
      xs: 'tw-btn-bordered-xs',
      sm: 'tw-btn-bordered-s',
      md: 'tw-btn-bordered-m',
      lg: 'tw-btn-bordered-l',
      xl: 'tw-btn-bordered-xl'
    }
  },
  compoundVariants: [{
    variant: 'neutral',
    size: 'xs',
    // This was necessary because the xs size sets the font-size 1.4rem and we need the text-size of tw-font-link1
    className: 'xs:!tw-font-link1'
  }],
  defaultVariants: {
    variant: 'primary',
    size: 'xs'
  }
});

/* defines the icon color for loading state */
var loadingIconItemClass = {
  primary: 'tw-bg-primary-light-100',
  'primary-inverted': 'tw-bg-primary-light-500',
  payment: 'tw-bg-neutral-light-800',
  'payment-inverted': 'tw-bg-success-500',
  deactivate: 'tw-bg-neutral-light-100',
  'deactivate-inverted': 'tw-bg-on-hold-500',
  "delete": 'tw-bg-neutral-light-100',
  'delete-inverted': 'tw-bg-alert-500',
  white: 'tw-bg-primary-light-500',
  cancel: 'tw-bg-neutral-light-700',
  icon: 'tw-bg-neutral-light-800',
  neutral: 'tw-bg-neutral-light-800'
};
var Button = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    size = _ref.size,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'primary' : _ref$variant,
    _ref$loading = _ref.loading,
    loading = _ref$loading === void 0 ? false : _ref$loading,
    loadingText = _ref.loadingText,
    _ref$inverse = _ref.inverse,
    inverse = _ref$inverse === void 0 ? false : _ref$inverse,
    _ref$asChild = _ref.asChild,
    asChild = _ref$asChild === void 0 ? false : _ref$asChild,
    children = _ref.children,
    props = _objectWithoutProperties(_ref, _excluded);
  var Comp = asChild ? Slot : 'button';
  var parsedVariant = inverse && variant !== 'white' && variant !== 'cancel' && variant !== 'neutral' && variant !== 'icon' ? "".concat(variant, "-inverted") : variant;
  return /*#__PURE__*/_jsx(Comp, _objectSpread(_objectSpread({
    ref: ref,
    className: buttonVariants({
      variant: parsedVariant,
      size: size,
      loading: loading,
      className: className
    })
  }, props), {}, {
    children: loading ? /*#__PURE__*/_jsxs("div", {
      className: "tw-flex tw-items-center",
      children: [/*#__PURE__*/_jsx(Loader, {
        size: "xs",
        className: "tw-fill",
        itemClass: loadingIconItemClass[parsedVariant]
      }), /*#__PURE__*/_jsx("div", {
        className: "tw-ml-0.8",
        children: loadingText || children
      })]
    }) : children
  }));
});
Button.displayName = 'Button';
export { Button, buttonVariants };